// Lead Management page - view and manage all leads
// Shows lead table with sorting and details modal


/**
 * 
 * NOTES SUMMARY - LEADS TABLE
 * 
 * What this page does:
 *  - Shows all leads in a table
 *  - Search box to filter by name/email/company
 *  - Dropdown to filter by stage
 *  - Clicking a lead opens modal with details
 *  - Refresh button reloads leads
 * 
 */




import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import Header from '../components/layout/Header';
import Sidebar from '../components/layout/Sidebar';
import LeadTable from '../components/leads/LeadTable';
import LeadModal from '../components/leads/LeadModal';
import Modal from '../components/common/Modal';
import { getLeads } from '../utils/api';
import { getActivityLevel, ACTIVITY_LEVELS } from '../utils/constants';
import '../styles/App.css';

function LeadManagement() {
  // State for leads and modal
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [interestFilter, setInterestFilter] = useState('all');
  const [activityFilter, setActivityFilter] = useState('all');
  const [campaignFilters, setCampaignFilters] = useState([]); // Changed to array for multi-select
  const [showUnqualified, setShowUnqualified] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const leadsPerPage = 50;
  const [modal, setModal] = useState({ isOpen: false, title: '', message: '', type: 'info' });
  
  // Bulk delete state
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  
  // Mobile menu state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };
  
  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  // Toggle campaign filter selection (multi-select)
  const toggleCampaignFilter = (campaignValue) => {
    setCampaignFilters(prev => {
      if (prev.includes(campaignValue)) {
        // Remove if already selected
        return prev.filter(c => c !== campaignValue);
      } else {
        // Add if not selected
        return [...prev, campaignValue];
      }
    });
    setCurrentPage(1);
  };

  // Clear all campaign filters
  const clearCampaignFilters = () => {
    setCampaignFilters([]);
    setCurrentPage(1);
  };

  // Load leads when component mounts
  useEffect(() => {
    loadLeads();
  }, []);
  
  // Function to load leads
  const loadLeads = async () => {
    setLoading(true);
    try {
      const data = await getLeads({ bustCache: true });
      setLeads(data.leads);
    } catch (error) {
      setModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to load leads: ' + error.message,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle lead click (open modal)
  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
  };
  
  // Handle modal close
  const handleCloseModal = () => {
    setSelectedLead(null);
  };
  
  // Handle lead update from modal
  const handleLeadUpdate = (updatedLead, action = 'update', leadId = null) => {
    if (action === 'delete' && leadId) {
      // Handle lead deletion - remove from leads array and selections
      setLeads(prevLeads => prevLeads.filter(lead => lead._id !== leadId));
      setSelectedLeads(prev => prev.filter(id => id !== leadId));
      setSelectedLead(null);
    } else if (updatedLead) {
      // Handle lead update - replace in leads array
      setLeads(prevLeads => 
        prevLeads.map(lead => 
          lead._id === updatedLead._id ? updatedLead : lead
        )
      );
      setSelectedLead(updatedLead);
    }
  };
  
  // Handle lead selection
  const handleLeadSelect = (leadId) => {
    setSelectedLeads(prev => {
      if (prev.includes(leadId)) {
        return prev.filter(id => id !== leadId);
      } else {
        return [...prev, leadId];
      }
    });
  };

  // Handle select all
  const handleSelectAll = (visibleLeadIds) => {
    const selectedVisibleCount = visibleLeadIds.filter(id => selectedLeads.includes(id)).length;
    
    if (selectedVisibleCount === visibleLeadIds.length) {
      // All visible leads are selected, so deselect them
      setSelectedLeads(prev => prev.filter(id => !visibleLeadIds.includes(id)));
    } else {
      // Not all visible leads are selected, so select all visible ones
      const newSelected = [...selectedLeads];
      visibleLeadIds.forEach(id => {
        if (!newSelected.includes(id)) {
          newSelected.push(id);
        }
      });
      setSelectedLeads(newSelected);
    }
  };

  // Handle bulk delete
  const handleBulkDelete = () => {
    if (selectedLeads.length > 0) {
      setShowBulkDeleteConfirm(true);
    }
  };

  // Confirm bulk delete
  const confirmBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      let successCount = 0;
      let errorCount = 0;
      
      // Delete leads one by one using existing endpoint
      for (const leadId of selectedLeads) {
        try {
          const response = await fetch('/.netlify/functions/delete-lead', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ leadId })
          });
          
          if (response.ok) {
            successCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          console.error(`Error deleting lead ${leadId}:`, error);
          errorCount++;
        }
      }
      
      // Update leads list
      setLeads(prev => prev.filter(lead => !selectedLeads.includes(lead._id)));
      setSelectedLeads([]);
      setShowBulkDeleteConfirm(false);
      
      // Show success/error message
      if (errorCount === 0) {
        setModal({
          isOpen: true,
          title: 'Success',
          message: `${successCount} leads deleted successfully`,
          type: 'success'
        });
      } else if (successCount === 0) {
        setModal({
          isOpen: true,
          title: 'Error',
          message: `Failed to delete ${errorCount} leads`,
          type: 'error'
        });
      } else {
        setModal({
          isOpen: true,
          title: 'Partial Success',
          message: `${successCount} leads deleted successfully, ${errorCount} failed`,
          type: 'warning'
        });
      }
      
    } catch (error) {
      setModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to delete leads: ' + error.message,
        type: 'error'
      });
    } finally {
      setBulkDeleting(false);
    }
  };
  
  // Get unique Instantly campaigns
  const instantlyCampaigns = [...new Set(leads
    .map(lead => lead.campaign_name)
    .filter(name => name)
  )].sort();

  // Get unique GHL campaigns (from tags array)
  const ghlCampaigns = [...new Set(leads
    .flatMap(lead => lead.ghl_tags || [])  // Flatten arrays of tags
    .filter(tag => tag)
  )].sort();

  // Filter leads based on search, stage, interest, activity, campaign, and unqualified status
  const filteredLeads = leads.filter(lead => {
    // Search filter
    const matchesSearch =
      lead.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.company?.toLowerCase().includes(searchTerm.toLowerCase());

    // Stage filter - handle 'assigned' specially
    const matchesStage = stageFilter === 'all' ||
      (stageFilter === 'assigned'
        ? (lead.assigned_to && lead.assigned_to !== 'Unassigned')
        : lead.stage === stageFilter);

    // Interest filter
    const matchesInterest = interestFilter === 'all' ||
      (lead.interest_status || 'unknown') === interestFilter;

    // Activity filter
    const matchesActivity = activityFilter === 'all' ||
      getActivityLevel(lead) === activityFilter;

    // Campaign filter - handle both Instantly and GHL campaigns with multi-select
    const matchesCampaign = campaignFilters.length === 0 || campaignFilters.some(filter => {
      if (filter.startsWith('instantly:')) {
        return lead.campaign_name === filter.replace('instantly:', '');
      } else if (filter.startsWith('ghl:')) {
        return (lead.ghl_tags || []).includes(filter.replace('ghl:', ''));
      }
      return false;
    });

    // Unqualified filter
    const matchesUnqualified = showUnqualified ? lead.stage === 'unqualified' : lead.stage !== 'unqualified';

    return matchesSearch && matchesStage && matchesInterest && matchesActivity && matchesCampaign && matchesUnqualified;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredLeads.length / leadsPerPage);
  const startIndex = (currentPage - 1) * leadsPerPage;
  const paginatedLeads = filteredLeads.slice(startIndex, startIndex + leadsPerPage);

  // Calculate activity counts for filter display
  const activityCounts = {
    all: filteredLeads.length,
    replied: leads.filter(lead => getActivityLevel(lead) === ACTIVITY_LEVELS.REPLIED).length,
    submitted: leads.filter(lead => getActivityLevel(lead) === ACTIVITY_LEVELS.SUBMITTED).length,
    clicked: leads.filter(lead => getActivityLevel(lead) === ACTIVITY_LEVELS.CLICKED).length,
    opened: leads.filter(lead => getActivityLevel(lead) === ACTIVITY_LEVELS.OPENED).length,
    inactive: leads.filter(lead => getActivityLevel(lead) === ACTIVITY_LEVELS.INACTIVE).length
  };

  // Calculate interest counts for filter display
  const interestCounts = {
    all: leads.length,
    interested: leads.filter(lead => lead.interest_status === 'interested').length,
    meeting_booked: leads.filter(lead => lead.interest_status === 'meeting_booked').length,
    meeting_completed: leads.filter(lead => lead.interest_status === 'meeting_completed').length,
    closed: leads.filter(lead => lead.interest_status === 'closed').length,
    neutral: leads.filter(lead => lead.interest_status === 'neutral').length,
    not_interested: leads.filter(lead => lead.interest_status === 'not_interested').length,
    do_not_contact: leads.filter(lead => lead.interest_status === 'do_not_contact').length,
    wrong_person: leads.filter(lead => lead.interest_status === 'wrong_person').length,
    out_of_office: leads.filter(lead => lead.interest_status === 'out_of_office').length,
    unknown: leads.filter(lead => !lead.interest_status || lead.interest_status === 'unknown').length
  };
  
  // Show loading state
  if (loading) {
    return (
      <div className="app-container">
        <Header onMenuClick={toggleSidebar} />
        <div className="app-body">
          <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
          <main className="app-main">
            <div className="loading">Loading leads...</div>
          </main>
        </div>
      </div>
    );
  }
  
  return (
    <div className="app-container">
      <Header onMenuClick={toggleSidebar} />
      <div className="app-body">
        <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
        <main className="app-main">
          <div className="page-header">
            <h1>Lead Management</h1>
            <div className="page-header-actions">
              {selectedLeads.length > 0 && (
                <button
                  onClick={handleBulkDelete}
                  className="btn btn-danger"
                  style={{ marginRight: '12px' }}
                >
                  <FontAwesomeIcon icon={faTrash} style={{ marginRight: '6px' }} />
                  Delete ({selectedLeads.length})
                </button>
              )}
              <button onClick={loadLeads} className="btn btn-secondary" disabled={loading}>
                {loading ? 'Loading...' : 'Refresh Leads'}
              </button>
            </div>
          </div>
          
          {/* Filters Section */}
          <div className="filters-section">
            {/* Search, Stage, and Interest - Side by Side */}
            <div className="filters-row">
              {/* Search Filter */}
              <div className="filter-group">
                <label className="filter-label">SEARCH:</label>
                <input
                  type="text"
                  className="search-input-large"
                  placeholder="Search by name, email, or company"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                />
              </div>

              {/* Stage Filter */}
              <div className="filter-group">
                <label className="filter-label">STAGE:</label>
                <select
                  className="stage-dropdown-large"
                  value={stageFilter}
                  onChange={(e) => { setStageFilter(e.target.value); setCurrentPage(1); }}
                >
                  <option value="all">All Stages</option>
                  <option value="cold">Cold</option>
                  <option value="engaged">Engaged</option>
                  <option value="qualified">Qualified</option>
                  <option value="assigned">Assigned</option>
                  <option value="unqualified">Unqualified</option>
                </select>
              </div>

              {/* Interest Filter */}
              <div className="filter-group">
                <label className="filter-label">INTEREST:</label>
                <select
                  className="stage-dropdown-large"
                  value={interestFilter}
                  onChange={(e) => { setInterestFilter(e.target.value); setCurrentPage(1); }}
                >
                  <option value="all">All Interest Levels</option>
                  <option value="interested">Interested ({interestCounts.interested})</option>
                  <option value="meeting_booked">Meeting Booked ({interestCounts.meeting_booked})</option>
                  <option value="meeting_completed">Meeting Completed ({interestCounts.meeting_completed})</option>
                  <option value="closed">Closed ({interestCounts.closed})</option>
                  <option value="neutral">Neutral ({interestCounts.neutral})</option>
                  <option value="not_interested">Not Interested ({interestCounts.not_interested})</option>
                  <option value="do_not_contact">Do Not Contact ({interestCounts.do_not_contact})</option>
                  <option value="wrong_person">Wrong Person ({interestCounts.wrong_person})</option>
                  <option value="out_of_office">Out of Office ({interestCounts.out_of_office})</option>
                  <option value="unknown">Unknown ({interestCounts.unknown})</option>
                </select>
              </div>

            </div>

            {/* Campaign Filter - Above Activity */}
            <div className="activity-filter-row" style={{ marginTop: '1rem', alignItems: 'flex-start' }}>
              <label className="filter-label">CAMPAIGNS:</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', flex: 1 }}>
                {/* Clear All Button */}
                {campaignFilters.length > 0 && (
                  <button
                    className="activity-tab"
                    onClick={clearCampaignFilters}
                    style={{ alignSelf: 'flex-start', fontSize: '0.875rem', padding: '0.25rem 0.75rem' }}
                  >
                    Clear Filters ({campaignFilters.length} selected)
                  </button>
                )}

                {/* Email Campaigns (Instantly) */}
                {instantlyCampaigns.length > 0 && (
                  <div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#6b7280', marginBottom: '0.25rem' }}>
                      EMAIL CAMPAIGNS (INSTANTLY)
                    </div>
                    <div className="activity-tabs" style={{ flexWrap: 'wrap' }}>
                      {instantlyCampaigns.map(campaign => {
                        const campaignValue = `instantly:${campaign}`;
                        const isSelected = campaignFilters.includes(campaignValue);
                        const count = leads.filter(l => l.campaign_name === campaign).length;
                        return (
                          <button
                            key={campaignValue}
                            className={`activity-tab ${isSelected ? 'active' : ''}`}
                            onClick={() => toggleCampaignFilter(campaignValue)}
                            style={{ fontSize: '0.875rem' }}
                          >
                            {campaign} ({count})
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* SMS Campaigns (GHL) */}
                {ghlCampaigns.length > 0 && (
                  <div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#6b7280', marginBottom: '0.25rem' }}>
                      SMS CAMPAIGNS (GO HIGH LEVEL)
                    </div>
                    <div className="activity-tabs" style={{ flexWrap: 'wrap' }}>
                      {ghlCampaigns.map(campaign => {
                        const campaignValue = `ghl:${campaign}`;
                        const isSelected = campaignFilters.includes(campaignValue);
                        const count = leads.filter(l => (l.ghl_tags || []).includes(campaign)).length;
                        return (
                          <button
                            key={campaignValue}
                            className={`activity-tab ${isSelected ? 'active' : ''}`}
                            onClick={() => toggleCampaignFilter(campaignValue)}
                            style={{ fontSize: '0.875rem' }}
                          >
                            {campaign} ({count})
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Activity Filter - Below Campaign */}
            <div className="activity-filter-row" style={{ marginTop: '1rem', alignSelf: 'flex-start', alignItems: 'flex-start' }}>
              <label className="filter-label">ACTIVITY:</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', flex: 1 }}>
                <div className="activity-tabs" style={{ flexWrap: 'wrap' }}>
                  <button
                    className={`activity-tab ${activityFilter === 'all' ? 'active' : ''}`}
                    onClick={() => { setActivityFilter('all'); setCurrentPage(1); }}
                  >
                    All Activity ({activityCounts.all})
                  </button>
                  <button
                    className={`activity-tab ${activityFilter === ACTIVITY_LEVELS.REPLIED ? 'active' : ''}`}
                    onClick={() => { setActivityFilter(ACTIVITY_LEVELS.REPLIED); setCurrentPage(1); }}
                  >
                    Replied ({activityCounts.replied})
                  </button>
                  <button
                    className={`activity-tab ${activityFilter === ACTIVITY_LEVELS.SUBMITTED ? 'active' : ''}`}
                    onClick={() => { setActivityFilter(ACTIVITY_LEVELS.SUBMITTED); setCurrentPage(1); }}
                  >
                    Submitted ({activityCounts.submitted})
                  </button>
                  <button
                    className={`activity-tab ${activityFilter === ACTIVITY_LEVELS.CLICKED ? 'active' : ''}`}
                    onClick={() => { setActivityFilter(ACTIVITY_LEVELS.CLICKED); setCurrentPage(1); }}
                  >
                    Clicked ({activityCounts.clicked})
                  </button>
                  <button
                    className={`activity-tab ${activityFilter === ACTIVITY_LEVELS.OPENED ? 'active' : ''}`}
                    onClick={() => { setActivityFilter(ACTIVITY_LEVELS.OPENED); setCurrentPage(1); }}
                  >
                    Opened ({activityCounts.opened})
                  </button>
                  <button
                    className={`activity-tab ${activityFilter === ACTIVITY_LEVELS.INACTIVE ? 'active' : ''}`}
                    onClick={() => { setActivityFilter(ACTIVITY_LEVELS.INACTIVE); setCurrentPage(1); }}
                  >
                    Inactive ({activityCounts.inactive})
                  </button>
                </div>
              </div>
            </div>

            {/* Status Row - Checkbox and Lead Count */}
            <div className="filter-status-row">
              <div className="checkbox-wrapper">
                <input
                  type="checkbox"
                  id="show-unqualified"
                  checked={showUnqualified}
                  onChange={(e) => { setShowUnqualified(e.target.checked); setCurrentPage(1); }}
                />
                <label htmlFor="show-unqualified">Show Unqualified Leads</label>
              </div>
              <div className="lead-count">
                <span className="active-count">{filteredLeads.length} Active</span>
                <span className="separator">•</span>
                <span className="hidden-count">
                  {leads.filter(l => l.stage === 'unqualified').length} unqualified hidden
                </span>
              </div>
            </div>
          </div>
          
          <LeadTable
                leads={paginatedLeads}
                onLeadClick={handleLeadClick}
                selectedLeads={selectedLeads}
                onLeadSelect={handleLeadSelect}
                onSelectAll={handleSelectAll}
              />

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '20px',
                  borderTop: '1px solid #e5e7eb'
                }}>
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      background: currentPage === 1 ? '#f3f4f6' : 'white',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      opacity: currentPage === 1 ? 0.5 : 1
                    }}
                  >
                    First
                  </button>
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      background: currentPage === 1 ? '#f3f4f6' : 'white',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      opacity: currentPage === 1 ? 0.5 : 1
                    }}
                  >
                    Previous
                  </button>
                  <span style={{ padding: '8px 16px', fontWeight: 500 }}>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      background: currentPage === totalPages ? '#f3f4f6' : 'white',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      opacity: currentPage === totalPages ? 0.5 : 1
                    }}
                  >
                    Next
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      background: currentPage === totalPages ? '#f3f4f6' : 'white',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      opacity: currentPage === totalPages ? 0.5 : 1
                    }}
                  >
                    Last
                  </button>
                  <span style={{ marginLeft: '12px', color: '#6b7280', fontSize: '14px' }}>
                    Showing {startIndex + 1}-{Math.min(startIndex + leadsPerPage, filteredLeads.length)} of {filteredLeads.length}
                  </span>
                </div>
              )}
          {/* Lead Modal (only shows if lead is selected) */}
          {selectedLead && (
            <LeadModal 
              lead={selectedLead} 
              onClose={handleCloseModal}
              onLeadUpdate={handleLeadUpdate}
            />
          )}
          
          {/* Bulk Delete Confirmation Modal */}
          {showBulkDeleteConfirm && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 99999
            }}>
              <div style={{
                backgroundColor: 'white',
                padding: '30px',
                borderRadius: '10px',
                textAlign: 'center'
              }}>
                <h2 style={{ marginBottom: '20px' }}>Delete {selectedLeads.length} leads?</h2>
                <button 
                  onClick={() => setShowBulkDeleteConfirm(false)}
                  disabled={bulkDeleting}
                  style={{ marginRight: '10px', padding: '10px 20px', cursor: bulkDeleting ? 'not-allowed' : 'pointer', opacity: bulkDeleting ? 0.5 : 1 }}
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmBulkDelete}
                  disabled={bulkDeleting}
                  style={{ 
                    padding: '10px 20px', 
                    backgroundColor: bulkDeleting ? '#999' : 'red', 
                    color: 'white', 
                    border: 'none', 
                    cursor: bulkDeleting ? 'not-allowed' : 'pointer',
                    opacity: bulkDeleting ? 0.8 : 1
                  }}
                >
                  {bulkDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          )}

          {/* Error Modal */}
          <Modal
            isOpen={modal.isOpen}
            onClose={() => setModal({ ...modal, isOpen: false })}
            title={modal.title}
            message={modal.message}
            type={modal.type}
          />
        </main>
      </div>
    </div>
  );
}

export default LeadManagement;